/**
 * Encode an AudioBuffer to a 16-bit PCM WAV Blob — small enough to ship
 * inline. We use this to convert MediaRecorder output (typically webm/opus)
 * into a format Suno's `/upload-cover` endpoint reliably ingests (mp3 / wav
 * / m4a).
 *
 * Reference: http://soundfile.sapp.org/doc/WaveFormat/
 *
 * Limitations (intentional — keeps it ~70 lines):
 *  - 16-bit PCM only (24-bit / float would double file size for no
 *    perceptible quality gain on a quick vocal scratch).
 *  - Mono if the source is mono; otherwise stereo. We don't downmix or
 *    resample — whatever the input AudioBuffer says, we honour.
 *
 * @param {AudioBuffer} audioBuffer — source samples
 * @param {object} [opts]
 * @param {number} [opts.gain=1] — linear multiplier baked into the output
 *   PCM before clamping to int16. Use Math.pow(10, dB/20) to convert from
 *   dB. Clamping prevents +N dB boosts from clipping silently — we hard
 *   clamp to ±1.0 (so +12 dB on already-loud input WILL clip; that's the
 *   honest behaviour expected of a gain knob).
 */
export function encodeWav(audioBuffer, opts = {}) {
  const gain = typeof opts.gain === "number" && Number.isFinite(opts.gain) ? opts.gain : 1;
  const bitDepth = opts.bitDepth === 24 ? 24 : 16;
  const numChannels = Math.max(1, audioBuffer.numberOfChannels);
  const sampleRate = audioBuffer.sampleRate;
  const numFrames = audioBuffer.length;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  // fmt chunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);            // chunk size
  view.setUint16(20, 1, true);             // PCM format = 1
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  // data chunk
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  // Interleave channels and clamp to int16 / int24. Doing this in one pass
  // to avoid temp allocations for long recordings.
  const channels = [];
  for (let c = 0; c < numChannels; c++) channels.push(audioBuffer.getChannelData(c));
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      let s = channels[c][i] * gain;
      if (s > 1) s = 1; else if (s < -1) s = -1;
      if (bitDepth === 24) {
        const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff);
        view.setUint8(offset, v & 0xff);
        view.setUint8(offset + 1, (v >> 8) & 0xff);
        view.setUint8(offset + 2, (v >> 16) & 0xff);
        offset += 3;
      } else {
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        offset += 2;
      }
    }
  }
  return new Blob([buffer], { type: "audio/wav" });
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}
